import { useLoaderData, Link, useSubmit, useRouteLoaderData } from "react-router-dom";
import { useMemo } from "react";
import Select from "react-select";
import DOMPurify from "dompurify";

import { useUploadStore } from "src/components/main/uploadStore";

import { json } from "src/components/common/types";
import { myAxios } from "src/components/common/functions";

import { field, form, Rule } from "../../types";

import { evaluateRules, getRuleTargets } from "./rules";
import { useFormState } from "./useFormState";
import { AsyncFormSelect } from "./AsyncFormSelect";
import { SignatureField } from "./SignatureField";
import { PhotoField } from "./PhotoField";

import { useUiStore } from "src/components/main/uiStore";
interface LoaderData {
	schema: {
		form: form;
		fields: field[];
	};
	values: Record<string, string | json[]>;
	record_id?: number;
    record_name?: string;
}

export default function Form({ mode }: { mode: "add" | "edit" }) {
	const submit = useSubmit();
	const upload = useUploadStore();
    const setShowLoader = useUiStore(state => state.setShowLoader);
	const editData = useRouteLoaderData("formRecord") as LoaderData;
	const addData = useLoaderData() as LoaderData;
	const { schema, values, record_id, record_name } = mode === "edit" ? editData : addData;
	const { formValues, setFormValues, selectValues, setSelectValues, fileValues, setFileValues } = useFormState(
		schema.fields,
		values
	);
	const rules = useMemo(() => (JSON.parse(schema.form.rules) ?? []) as Rule[], [schema.form.rules]);
	const ruleTargets = useMemo(() => getRuleTargets(rules), [rules]);
	const visibleByRules = useMemo(() => evaluateRules(rules, formValues), [rules, formValues]);
	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = e.currentTarget as HTMLFormElement;
		if(!form.reportValidity()) return;
		let mapFieldkeyToPicturesId = {} as json;
		const cb = () => {
			const fd = new FormData(form);
			for (const [fieldKey, id] of Object.entries(mapFieldkeyToPicturesId)){
				fd.append(fieldKey, id);
			}
			for (let field of schema.fields) {
				if (["checkbox", "signature", "picture"].includes(field.field_type) && field.is_required && !fd.get(String(field.field_key))) {
					window.alert(field.label + " is required.");
					return;
				}
			}
			fd.append("formId", String(schema.form.id));
			if (mode === "edit") {
				fd.append("record_id", String(record_id));
			}
			submit(fd, { method: "post" });
		}
		upload.setOnResetCallback(() => {
			cb();
		});
		const formData = new FormData();
		const totalFiles = [] as File[]; // ???
		for (let i = 0; i < Object.entries(fileValues).length; i++) {
			const fileValue = Object.entries(fileValues)[i];
			const [fieldKeyStr, items] = fileValue;
			if (!items.length) continue;
			totalFiles.push(...items.map(f => f.file));
			if(formValues[fieldKeyStr]) mapFieldkeyToPicturesId[fieldKeyStr] = formValues[fieldKeyStr];
			for(const item of items){
				formData.append("parentId[]", mapFieldkeyToPicturesId[fieldKeyStr] ?? ("new" + fieldKeyStr));
				formData.append("fileId[]", item.id);
				formData.append("caption[]", item.caption ?? "");
				formData.append("fileName[]", item.file?.name ?? "");
				formData.append("fileSize[]", String(item.file?.size ?? ""));
				formData.append("fileType[]", item.file?.type ?? "");
				formData.append("lastModifiedAt[]", item.file?.lastModified ? (new Date(item.file.lastModified)).toISOString().slice(0, 19).replace('T', ' ') : "");
				//------------------------
			}
		}
		if(totalFiles.length){
			setShowLoader(true);
			const preRes = await myAxios({ fileName: "forms/uploadPictures.php", formData: formData });
			setShowLoader(false);
			if(!preRes) return;
			mapFieldkeyToPicturesId = {...mapFieldkeyToPicturesId, ...preRes.data.map};
			upload.setFiles(Array.from(totalFiles.filter(file => file)));
			upload.setUrls(preRes.data.urls);
			upload.setIds(preRes.data.ids);
			upload.setShow(true);
		}else{
			cb();
		}
	}
	return (
		<div className = "container py-3">
			<h3>{mode === "add" ? `New ${schema.form.name}` : `Edit ${schema.form.name}`}</h3>
			<form onSubmit = {handleSubmit}>
                <div className = "mb-3">
                    <label children = "Record Name" htmlFor = "record_name" className = "form-label"/>
                    <input type = "text" id = "record_name" name = "record_name" defaultValue = {record_name ?? ""} className = "form-control"/>
				</div>
                {schema.fields.map((field) => {
					const key = String(field.field_key);
					// ---- RULE VISIBILITY (fields in then.show are hidden by default) ----
					if (rules.length && ruleTargets.has(field.field_key) && !visibleByRules.has(field.field_key)) {
						return null;
					}
					// --------------------------------------------------------------------
					return ["text", "search", "url", "tel", "email", "password", "number", "date", "time", "month", "week", "datetime-local", "range"].includes(
						field.field_type
					) ? (
						<div key = {key} className = "mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<input
								placeholder = {["text", "search", "url", "tel", "email", "password", "number"].includes(field.field_type) ? field.placeholder ?? "" : ""}
								required = {!!field.is_required}
								min = {field.min ?? ""}
								max = {field.max ?? ""}
								type = {field.field_type}
								className = "form-control"
								id = {key}
								name = {key}
								value = {formValues[key] ?? ""}
								onChange = {(e) =>
									setFormValues((v) => ({
										...v,
										[key]: (e.target as HTMLInputElement).value
									}))
								}
							/>
							{field.field_type === "range" ? <span>{formValues[key]}</span> : null}
						</div>
					) : field.field_type === "textarea" ? (
						<div key = {key} className = "mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<textarea
								placeholder = {field.placeholder ?? ""}
								required = {!!field.is_required}
								className = "form-control"
								id = {key}
								name = {key}
								value = {formValues[key] ?? ""}
								onChange = {(e) =>
									setFormValues((v) => ({
										...v,
										[key]: e.target.value
									}))
								}
							/>
						</div>
					) : field.field_type === "select" ? (
						<div key = {key} className = "mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<select
								required = {!!field.is_required}
								className = "form-control"
								id = {key}
								name = {key}
								value = {formValues[key] ?? ""}
								onChange = {(e) =>
									setFormValues((v) => ({
										...v,
										[key]: e.target.value
									}))
								}
							>
								<option value = "">Select</option>
								{field.options?.map((opt, i) => (
									<option key = {i}>{opt}</option>
								))}
							</select>
						</div>
					) : field.field_type === "radio" ? (
						<div key = {key} className = "mb-3">
							<span children = {field.label} className = {"text-nowrap d-block mb-2" + (!!field.is_required ? " required-field" : "")}/>
							{field.options?.map((opt, i) => (
								<span key = {i} className = "me-3">
									<label className = "text-nowrap me-2" htmlFor = {key + opt}>
										{opt}
									</label>
									<input
										type = "radio"
										required = {!!field.is_required}
										className = "cssCheckBox"
										id = {key + opt}
										name = {key}
										value = {opt}
										checked = {formValues[key] === opt}
										onChange = {() =>
											setFormValues((v) => ({
												...v,
												[key]: opt
											}))
										}
									/>
								</span>
							))}
							{!field.is_required && <button children = "Clear" type = "button"  className = "btn btn-danger" onClick = {() => setFormValues((v) => ({ ...v, [key]: "" }))}/>}
						</div>
					) : field.field_type === "multiSelect" ? (
						<div key = {key} className = "mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<Select
								inputId = {key}
								required = {!!field.is_required}
								isMulti
								options = {field.options?.map((opt) => ({ label: opt, value: opt }))}
								value = {selectValues[key] ?? []}
								onChange = {(e) => {
									setSelectValues((s) => ({
										...s,
										[key]: e as any
									}));
								}}
								classNamePrefix = "react-select"
								closeMenuOnSelect
							/>
							{(selectValues[key] ?? []).map((opt) => (
								<input key = {opt.value} type = "hidden" name = {key} value = {opt.value} />
							))}
						</div>
					) : field.field_type === "checkbox" ? (
						<div key = {key} className = "mb-3">
							<span children = {field.label} className = {"form-label text-nowrap d-block" + (!!field.is_required ? " required-field" : "")}/>
							{field.options?.map((opt, i) => (
								<span key = {i} className = "me-3">
									<label className = "text-nowrap me-2" htmlFor = {key + opt}>
										{opt}
									</label>
									<input
										type = "checkbox"
										className = "cssCheckBox"
										id = {key + opt}
										name = {key}
										value = {opt}
										checked = {(formValues[key] ?? []).includes(opt)}
										onChange = {(e) =>
											setFormValues((v) => ({
												...v,
												[key]: e.target.checked
													? [...(v[key] ?? []), opt]
													: (v[key] ?? []).filter((o: string) => o !== opt)
											}))
										}
									/>
								</span>
							))}
						</div>
					): field.field_type === "sub_form" && field.sub_form_id? (
						<div key = {key} className="mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<AsyncFormSelect
						   		isMulti = {true}
								placeholder = "Search sub form records…"
						   		// required = {!!field.is_required} --- IGNORE ---
								name = {key}
								form_id = {field.sub_form_id}
						   		inputId = {key}
								fileName = "forms/searchRecord.php"
                                value = {formValues[key]}
                                onChange = {(next) => 
                                    setFormValues(v => ({
                                        ...v,
                                        [key]: next
                                    }))
                                }
							/>
						</div>
					) : ["user", "users"].includes(field.field_type) ? (
						<div key = {key} className="mb-3">
							<label children = {field.label} className = {"form-label text-nowrap" + (!!field.is_required ? " required-field" : "")} htmlFor = {key}/>
							<AsyncFormSelect
								isMulti = {field.field_type === "users"}
								placeholder = "Search user…"
								required = {!!field.is_required}
								name = {key}
								// form_id not needed for user search --- IGNORE ---
								inputId = {key}
								fileName = "common/searchUser.php"
								value = {formValues[key]}
                                onChange = {(next) => 
                                    setFormValues(v => ({
                                        ...v,
                                        [key]: next
                                    }))
                                }
							/>
						</div>
					)  : field.field_type === "display" ? (
						<div
							key = {key}
							dangerouslySetInnerHTML = {{
								__html: DOMPurify.sanitize(field.label ?? "", {
									ALLOWED_TAGS: ["div", "b", "i", "strong", "em", "u", "br", "p", "ul", "ol", "li"],
									ALLOWED_ATTR: ["class"]
								})
							}}
						/>
					)  : field.field_type === "signature" ? (
						<div key = {key} className = "mb-3">
							<span children = {field.label} className = {"text-nowrap d-block mb-2" + (!!field.is_required ? " required-field" : "")}/>
							<SignatureField
								value = {formValues[key] ?? ""}
								onChange = {(next:string) =>
									setFormValues((v) => ({
										...v,
										[key]: next
									}))
								}
								required = {field.is_required === 1}
							/>
							<input type = "hidden" name = {key} value = {formValues[key] ?? ""}/>
						</div>
					) : field.field_type === "picture" ? (
						<div key = {key} className = "mb-3">
							<span children = {field.label} className = {"text-nowrap d-block mb-2" + (!!field.is_required ? " required-field" : "")}/>
							<PhotoField
								fieldKey = {field.field_key}
								required = {!!field.is_required}
								// maxFiles = {field.maxFiles}
								value = {(fileValues[field.field_key] ?? [])}
								onChange = {(files) => {
									setFileValues((prev) => ({
										...prev,
										[field.field_key]: files
									}));
								}}
							/>
						</div>
					) : null;
				})}
				<button className = "btn btn-primary me-3">{mode === "add" ? "Create" : "Update"}</button>
				<Link to = ".." className="btn btn-outline-secondary me-3">
					Back
				</Link>
			</form>
		</div>
	);
}